import { camelToKebab, chains } from "@silo/types";
import type { TrueKeys, ChainKey, Address } from "@silo/types";
import { satisfies } from "effect/Function";
import { match, P } from "ts-pattern";

/**
 * If a chain follows the EIP-3091 standard, then explorer links follow the same
 * convention. If not, explorer link logic must be manually implemented.
 *
 * https://eips.ethereum.org/EIPS/eip-3091
 */
const eip3091Config = satisfies<Record<ChainKey, boolean>>()({
  ethereum: true,
  arbitrum: true,
  optimism: true,
  base: true,
  local: false,
  anvil: false,
});

type Eip3091Key = TrueKeys<typeof eip3091Config>;

function isEip3091Key(key: ChainKey): key is Eip3091Key {
  return eip3091Config[key];
}

export function getAccountUrl(key: ChainKey, account: Address): string {
  const chain = chains[key];

  return match(key)
    .when(
      isEip3091Key,
      () => `${chain.blockExplorers.default.url}/address/${account}`
    )
    .with(P.union("anvil", "local"), () => "")
    .exhaustive();
}

export function getTxUrl(key: ChainKey, txHash: string): string {
  const chain = chains[key];

  return match(key)
    .when(
      isEip3091Key,
      () => `${chain.blockExplorers.default.url}/tx/${txHash}`
    )
    .with(P.union("anvil", "local"), () => "")
    .exhaustive();
}

export function getTokenUrl(key: ChainKey, tokenAddress: Address): string {
  const chain = chains[key];

  return match(key)
    .when(
      isEip3091Key,
      () => `${chain.blockExplorers.default.url}/token/${tokenAddress}`
    )
    .with(P.union("anvil", "local"), () => "")
    .exhaustive();
}

export function getChainSlug(chainKey: ChainKey): string {
  return camelToKebab(chainKey);
}
